import {
    AppDispatch,
    RootState,
} from '../../store';
import { createListenerMiddleware } from '@reduxjs/toolkit';
import {
    changeCaseSize,
    changeColor,
    goToBasket,
    selectCurrentCaseSize,
    selectCurrentColor,
    selectCustomerConfig,
    setAtomicId,
    setHardwareOnly,
    setInitialState,
    toggleAccordionItem,
    updateStorage as updateStorageAction,
} from 'Container/App/slice';
import {
    getDeviceDetailGroup,
    getTariffWithHardware,
} from 'Container/App/api/glados';
import {
    SAILS_PARAM_PAYMENT_METHOD,
    updateStorage,
} from '@vfde-sails/storage';
import { SailsStoneDdpStorage } from 'Container/App/interface';
import {
    SAILS_MULTICARD_STORAGE,
    SAILS_PARAM_ATOMIC_ID,
    SAILS_PARAM_DEVICE_ID,
    SAILS_PARAM_HARDWAREONLY,
    SAILS_PARAM_SALESCHANNEL,
    URL_PARAM_ATOMIC_ID,
} from '@vfde-sails/constants';
import { APP } from 'Constant';
import {
    CONTAINER,
    MTAN_PAGE,
} from 'Container/App/constants';
import {
    TRACKING_INFORMATION_ACTION_REVEAL,
    TrackingInformationTrigger,
    TrackingInformationUiType,
    trackIt,
    TrackType,
} from '@vfde-sails/tracking';
import { createPageViewTrackingData } from 'Container/App/helpers/tracking';
import {
    selectAtomicDevices,
    selectCaseSizesForColor,
    selectSubscription,
    selectTrackingPayload,
} from 'Container/App/selectors';
import { getNewSelectedCaseSize } from 'Container/App/helpers/caseSizeHelper';
import {
    createUrlWithQueryString,
    decodeHtml,
    hasQueryParam,
    updateUrl,
} from '@vfde-sails/utils';
import generateDeeplinkHelper from 'Container/App/helpers/generateDeeplinkHelper';

const stoneDdpListener = createListenerMiddleware();

export const startAppListening = stoneDdpListener.startListening.withTypes<
    RootState,
    AppDispatch
>();

startAppListening({
    actionCreator: setInitialState,
    effect: async (action, listenerApi) => {
        // Can cancel other running instances
        listenerApi.cancelActiveListeners();

        const { deviceId } = action.payload;

        listenerApi.dispatch(getDeviceDetailGroup.initiate(deviceId));
    },
});

startAppListening({
    predicate: (action, currentState, originalState) =>
        selectCustomerConfig(currentState) !== selectCustomerConfig(originalState),
    effect: (action, listenerApi) => {
        // Can cancel other running instances
        listenerApi.cancelActiveListeners();

        listenerApi.dispatch(updateStorageAction());
    },
});

startAppListening({
    matcher: getDeviceDetailGroup.matchFulfilled,
    effect: async (action, listenerApi) => {
        listenerApi.cancelActiveListeners();

        const { isHardwareOnly, paymentMethod } = selectCustomerConfig(listenerApi.getState());

        if (isHardwareOnly === false || paymentMethod !== undefined) {
            // We have to wait, or tracking below fails
            await listenerApi.dispatch(getTariffWithHardware.initiate(action.payload.atomics.map(atomic => atomic.atomicId)));
        }

        const trackingData = selectTrackingPayload(listenerApi.getState());

        const trackingPayload = createPageViewTrackingData(trackingData);

        trackIt(TrackType.PageView, trackingPayload);
    },
});

/**
 * Update the storage whenever the customer config changes
 */
startAppListening({
    actionCreator: updateStorageAction,
    effect: (action, api) => {
        api.cancelActiveListeners();

        const {
            deviceId,
            atomicId,
            isHardwareOnly,
            salesChannel,
            paymentMethod,
        } = selectCustomerConfig(api.getState());

        updateStorage<SailsStoneDdpStorage>(SAILS_MULTICARD_STORAGE, {
            [SAILS_PARAM_SALESCHANNEL]: salesChannel,
            [SAILS_PARAM_ATOMIC_ID]: atomicId || null,
            [SAILS_PARAM_DEVICE_ID]: deviceId || null,
            [SAILS_PARAM_HARDWAREONLY]: isHardwareOnly,
            [SAILS_PARAM_PAYMENT_METHOD]: paymentMethod,
            [APP]: {
                [CONTAINER]: {
                    [deviceId!]: {
                        [SAILS_PARAM_HARDWAREONLY]: isHardwareOnly,
                    },
                },
            },
        }, { shouldDeepMerge: true });
    },
});

/**
 * Change color
 */
startAppListening({
    actionCreator: changeColor,
    effect: (action, api) => {
        api.cancelActiveListeners();

        const [
            currentCaseSize,
            capacitiesForColor,
        ] = [
            selectCurrentCaseSize(api.getState()),
            selectCaseSizesForColor(api.getState()),
        ];

        const newCaseSize = getNewSelectedCaseSize(capacitiesForColor!, currentCaseSize!);

        api.dispatch(changeCaseSize(newCaseSize));
    },
});

/**
 * Change case size
 */
startAppListening({
    actionCreator: changeCaseSize,
    effect: (action, api) => {
        api.cancelActiveListeners();

        const [currentColor, currentCaseSize, atomics] = [
            selectCurrentColor(api.getState()),
            selectCurrentCaseSize(api.getState()),
            selectAtomicDevices(api.getState()),
        ];

        const newAtomicDevice = atomics!
            .filter(atomic => {
                const { caseSize, color } = atomic;

                return color.displayLabel === currentColor && caseSize === currentCaseSize;
            })[0];

        api.dispatch(setAtomicId(newAtomicDevice.atomicId));
    },
});

/**
 * Do things after customer selected an atomic
 */
startAppListening({
    actionCreator: setAtomicId,
    effect: (action, api) => {
        api.cancelActiveListeners();

        const [
            { atomicId },
            trackingData,
        ] = [
            selectCustomerConfig(api.getState()),
            selectTrackingPayload(api.getState()),
        ];

        if (hasQueryParam(URL_PARAM_ATOMIC_ID)) {
            updateUrl(createUrlWithQueryString(URL_PARAM_ATOMIC_ID, `${atomicId!}`), true);
        }

        const trackingPayload = createPageViewTrackingData(trackingData);

        trackIt(TrackType.PageView, trackingPayload);
    },
});

/**
 * Handle choice of payment
 * Triggers getSubscription if customer wants to pay monthly
 */
startAppListening({
    actionCreator: setHardwareOnly,
    effect: (action, api) => {
        const [
            { isHardwareOnly },
            subscription,
            atomics,
        ] = [
            selectCustomerConfig(api.getState()),
            selectSubscription(api.getState()),
            selectAtomicDevices(api.getState()),
        ];

        if (isHardwareOnly === false && !subscription) {
            api.dispatch(getTariffWithHardware.initiate(atomics.map(atomic => atomic.atomicId)));
        }
    },
});

/**
 * Generate deeplink for basket and redirect the user to the basket (depending on the configuration via mtan page)
 */
startAppListening({
    actionCreator: goToBasket,
    effect: (action, api) => {
        const customerConfig = selectCustomerConfig(api.getState());
        const subscription = selectSubscription(api.getState());

        const deeplink = generateDeeplinkHelper(customerConfig, subscription);

        let url: URL;

        if (customerConfig.isHardwareOnly) {
            url = new URL(`${window.location.origin}${deeplink}`);
        }
        else {
            url = new URL(window.location.origin + MTAN_PAGE);
            url.searchParams.append('goto', deeplink);
        }

        window.location.assign(url.toString());
    },
});

/**
 * Track opening of accordion items
 */
startAppListening({
    actionCreator: toggleAccordionItem,
    effect: (action, api) => {
        api.cancelActiveListeners();

        if (!action.payload.optOpen) {
            return;
        }

        const trackingPayload = {
            /* eslint-disable camelcase, @typescript-eslint/naming-convention */
            information_action: TRACKING_INFORMATION_ACTION_REVEAL,
            information_ui_type: TrackingInformationUiType.Faq,
            information_trigger: TrackingInformationTrigger.Accordion,
            information_name: decodeHtml(action.payload.stdHeadline),
            /* eslint-enable camelcase, @typescript-eslint/naming-convention */
        };

        trackIt(TrackType.Information, trackingPayload);
    },
});

export default stoneDdpListener;
